import React, {useState} from 'react';
import {
    Show,
    useGetIdentity,
    useNotify,
    usePermissions,
    useRecordContext,
    useRedirect,
    useRefresh,
    useTranslate,
} from 'react-admin';
import {Link as RouterLink} from 'react-router-dom';
import {Alert, AlertTitle, Avatar, Box, TextField as MuiTextField, Typography} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    EditOutlined,
    DeleteOutline,
    ThumbUpAltOutlined,
    ThumbDownAltOutlined,
    PrintOutlined,
    FavoriteBorder,
    ChatBubbleOutline,
    Visibility,
    TextSnippet,
    ContentCopyOutlined,
} from '@mui/icons-material';
import ReactPlayer from 'react-player';
import moment from 'moment';
import {t} from '../themes/statamicTokens';
import PageHeader from '../components/statamic/PageHeader';
import PrimaryButton from '../components/statamic/PrimaryButton';
import SectionCard from '../components/statamic/SectionCard';
import DataRow from '../components/statamic/DataRow';
import StatusDot from '../components/statamic/StatusDot';
import StatusPill from '../components/statamic/StatusPill';
import StatamicTabs, {useTabs} from '../components/statamic/StatamicTabs';
import DeleteConfirmDialog from '../components/statamic/DeleteConfirmDialog';
import canDeletePost from './canDeletePost';
import useDeleteConfirm from '../components/statamic/useDeleteConfirm';
import ApprovalDialog from '../components/statamic/ApprovalDialog';
import PostComments from './PostComments';
import TranslationTable from '../components/TranslationTable';
import {axiosApiInstance} from '../AxiosUtils';
import hasPermission from '../auth/hasPermission';

const statusVariant = (status) => {
    switch (status) {
        case 'PUBLISHED':        return 'success';
        case 'PENDING_APPROVAL': return 'warning';
        case 'REJECTED':         return 'danger';
        default:                 return 'neutral';
    }
};

const statusLabel = (status, translate) => {
    switch (status) {
        case 'PUBLISHED':        return translate('Published');
        case 'PENDING_APPROVAL': return translate('Pending Approval');
        case 'REJECTED':         return translate('Rejected');
        default:                 return status ?? '';
    }
};

const MediaBlock = ({record, onPrint, translate}) => {
    const isTextPost = record.message_type === 'TEXT';
    const isVideoPost = record.media_type === 'video' || record.message_type === 'VIDEO';
    const isPhotoPost = record.media_type === 'photo' || record.message_type === 'PHOTO';

    if (isTextPost) {
        return (
            <SectionCard>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 6,
                        px: 4,
                        backgroundColor: t.color.sidebarHover,
                        borderRadius: `${t.radius.md}px`,
                    }}
                >
                    <TextSnippet sx={{fontSize: 32, color: t.color.text.muted, mb: 2}}/>
                    <Typography
                        sx={{
                            fontSize: 20,
                            lineHeight: 1.55,
                            color: t.color.text.primary,
                            textAlign: 'center',
                            maxWidth: 680,
                        }}
                    >
                        {record.description}
                    </Typography>
                </Box>
            </SectionCard>
        );
    }

    return (
        <SectionCard>
            {isPhotoPost && (
                <Box sx={{position: 'relative'}}>
                    <Box
                        component="img"
                        src={`${process.env.REACT_APP_CDN_URL}/${record.name}.${record.ext}`}
                        alt={record.description || 'Post photo'}
                        sx={{
                            width: '100%',
                            maxHeight: 560,
                            objectFit: 'contain',
                            display: 'block',
                            borderRadius: `${t.radius.sm}px`,
                            backgroundColor: '#000',
                        }}
                    />
                    <Box sx={{position: 'absolute', top: 8, right: 8}}>
                        <PrimaryButton
                            variant="secondary"
                            startIcon={<PrintOutlined sx={{fontSize: 16}}/>}
                            onClick={onPrint}
                            sx={{backgroundColor: 'rgba(255,255,255,0.95)'}}
                        >
                            {translate('Print Photo')}
                        </PrimaryButton>
                    </Box>
                </Box>
            )}
            {isVideoPost && (
                <Box sx={{borderRadius: `${t.radius.sm}px`, overflow: 'hidden', backgroundColor: '#000'}}>
                    <ReactPlayer
                        url={`${process.env.REACT_APP_CDN_URL}/${record.name}.${record.ext}`}
                        controls
                        width="100%"
                        height="auto"
                    />
                </Box>
            )}
            {record.description && (
                <Typography
                    sx={{
                        mt: 2,
                        fontSize: `${t.type.body.size}px`,
                        color: t.color.text.primary,
                        lineHeight: 1.55,
                        whiteSpace: 'pre-wrap',
                    }}
                >
                    {record.description}
                </Typography>
            )}
        </SectionCard>
    );
};

const MetricRow = ({icon: Icon, label, value}) => (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75}}>
        <Icon sx={{fontSize: 16, color: t.color.text.muted}}/>
        <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.text.primary, flex: 1}}>
            {label}
        </Typography>
        <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 600, color: t.color.text.primary}}>
            {value}
        </Typography>
    </Box>
);

const DuplicatePostAction = ({record, translate}) => {
    const [childId, setChildId] = useState('');
    const [duplicating, setDuplicating] = useState(false);
    const [status, setStatus] = useState(null);

    const duplicate = () => {
        if (!record || !childId) return;
        setDuplicating(true);
        setStatus(null);
        axiosApiInstance.post(
            `${process.env.REACT_APP_API_URL}/api/v1/admin/post/duplicate/${record.id}/${childId}`
        ).then((response) => {
            setDuplicating(false);
            setStatus(response.status === 200 ? 'success' : 'error');
        }).catch(() => {
            setDuplicating(false);
            setStatus('error');
        });
    };

    return (
        <SectionCard title={translate('Duplicate')} subtitle={translate('Duplicate this post to another child')}>
            <MuiTextField
                size="small"
                fullWidth
                label={translate('Duplicate for child ID')}
                value={childId}
                onChange={(e) => setChildId(e.target.value)}
                sx={{'& .MuiInputBase-input': {fontSize: `${t.type.body.size}px`}}}
            />
            <LoadingButton
                onClick={duplicate}
                loading={duplicating}
                disabled={!childId}
                variant="outlined"
                size="small"
                startIcon={<ContentCopyOutlined sx={{fontSize: 16}}/>}
                sx={{
                    mt: 1,
                    textTransform: 'none',
                    fontSize: `${t.type.small.size}px`,
                    borderColor: t.color.border.default,
                    color: t.color.text.primary,
                    '&:hover': {borderColor: t.color.accent.primary, color: t.color.accent.primary, backgroundColor: 'transparent'},
                }}
            >
                {translate('Duplicate')}
            </LoadingButton>
            {status === 'success' && (
                <Alert severity="success" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Success</AlertTitle>
                    {translate('Post is duplicated')}
                </Alert>
            )}
            {status === 'error' && (
                <Alert severity="warning" sx={{mt: 1, fontSize: `${t.type.small.size}px`}}>
                    <AlertTitle sx={{fontSize: `${t.type.body.size}px`}}>Warning</AlertTitle>
                    {translate('Not able to duplicate')}
                </Alert>
            )}
        </SectionCard>
    );
};

const PostShowContent = () => {
    const record = useRecordContext();
    const redirect = useRedirect();
    const refresh = useRefresh();
    const notify = useNotify();
    const translate = useTranslate();
    const {permissions} = usePermissions();
    const {identity} = useGetIdentity();
    const deleteConfirm = useDeleteConfirm();
    const [tab, setTab] = useTabs('comments');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogAction, setDialogAction] = useState('approve');
    const [dialogPending, setDialogPending] = useState(false);

    if (!record) return null;

    const canManageStatus = (permissions === 'admin' || permissions === 'umbrella') && record.status;
    const isPhotoPost = record.media_type === 'photo' || record.message_type === 'PHOTO';

    const handleStatusChange = async (action, reason) => {
        setDialogPending(true);
        try {
            const rolePath = permissions === 'admin' ? 'admin' : 'umbrellad';
            const body = action === 'reject' ? {reason} : {};
            await axiosApiInstance.put(
                `${process.env.REACT_APP_API_URL}/api/v1/${rolePath}/posts/${record.id}/${action}`,
                body
            );
            notify(`Post ${action === 'approve' ? 'approved' : 'rejected'} successfully`, {type: 'success'});
            refresh();
            setDialogOpen(false);
        } catch (e) {
            notify(`Failed to ${action} post: ${e?.message || 'unknown error'}`, {type: 'error'});
        } finally {
            setDialogPending(false);
        }
    };

    const handlePrintPhoto = () => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;
        const doc = printWindow.document;

        const makeMeta = (labelText, valueText) => {
            const el = doc.createElement('div');
            el.className = 'info';
            el.appendChild(doc.createTextNode(`${labelText}: ${valueText}`));
            return el;
        };

        // Head
        const title = doc.createElement('title');
        title.textContent = `Print Update - ${record.child?.name ?? ''}`;
        doc.head.appendChild(title);

        const style = doc.createElement('style');
        style.textContent = [
            'body{font-family:Inter,Arial,sans-serif;margin:20px;color:#111}',
            '.container{max-width:800px;margin:0 auto;padding:20px}',
            '.header{text-align:center;margin-bottom:30px;padding-bottom:20px;border-bottom:1px solid #e5e7eb}',
            '.header h1{margin:10px 0;color:#111;font-size:24px;font-weight:700}',
            '.header .info{color:#6b7280;font-size:13px;margin:4px 0}',
            '.image-container{text-align:center;margin:24px 0}',
            '.image-container img{max-width:100%;max-height:560px;border-radius:6px;border:1px solid #e5e7eb}',
            '.message{margin:24px 0;padding:16px;background:#fafafa;border-left:3px solid #84ba3f;border-radius:4px}',
            '.message h2{margin-top:0;color:#111;font-size:15px;font-weight:600}',
            '.message p{line-height:1.6;font-size:14px;margin:6px 0;white-space:pre-wrap}',
            '.print-button{position:fixed;top:16px;right:16px;padding:8px 14px;background:#84ba3f;color:white;border:none;border-radius:4px;cursor:pointer;font-size:13px}',
            '@media print{.no-print{display:none}}',
        ].join('');
        doc.head.appendChild(style);

        // Body
        const printBtn = doc.createElement('button');
        printBtn.className = 'print-button no-print';
        printBtn.textContent = 'Print';
        printBtn.addEventListener('click', () => printWindow.print());
        doc.body.appendChild(printBtn);

        const container = doc.createElement('div');
        container.className = 'container';

        const header = doc.createElement('div');
        header.className = 'header';
        const h1 = doc.createElement('h1');
        h1.textContent = `Update for ${record.child?.name ?? ''}`;
        header.appendChild(h1);

        const creatorName = record.creator?.given_name ?? '';
        const charityName = record.child?.charity?.charity_name ?? '';
        const fromText = record.creator?.is_volunteer && charityName
            ? `${creatorName} (${charityName})`
            : creatorName;
        header.appendChild(makeMeta('From', fromText));
        header.appendChild(makeMeta('Date', moment(record.created_time).format('LL')));
        container.appendChild(header);

        const imgContainer = doc.createElement('div');
        imgContainer.className = 'image-container';
        const img = doc.createElement('img');
        img.src = `${process.env.REACT_APP_CDN_URL}/${record.name}.${record.ext}`;
        img.alt = 'Update photo';
        imgContainer.appendChild(img);
        container.appendChild(imgContainer);

        const description = record.description || '';
        if (description) {
            const msg = doc.createElement('div');
            msg.className = 'message';
            const h2 = doc.createElement('h2');
            h2.textContent = 'Message';
            msg.appendChild(h2);
            // Split on newlines to emit <br> without innerHTML
            const p = doc.createElement('p');
            const lines = description.split('\n');
            lines.forEach((line, i) => {
                p.appendChild(doc.createTextNode(line));
                if (i < lines.length - 1) p.appendChild(doc.createElement('br'));
            });
            msg.appendChild(p);
            container.appendChild(msg);
        }

        doc.body.appendChild(container);
        doc.close();
    };

    const tabs = [
        {id: 'comments',     label: translate('Comments'), count: record.nr_of_comments ?? 0},
        {id: 'translations', label: translate('Translations')},
    ];

    return (
        <Box sx={{backgroundColor: t.color.page, minHeight: '100%'}}>
            <PageHeader
                breadcrumb={{label: translate('Posts'), to: '/posts'}}
                title={
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <StatusDot variant={statusVariant(record.status)}/>
                        <Box component="span">
                            {record.creator?.given_name}
                            <Box component="span" sx={{color: t.color.text.secondary, fontWeight: 400}}>
                                {' '}· {moment(record.created_time).fromNow()}
                            </Box>
                        </Box>
                    </Box>
                }
                actions={
                    <>
                        {canManageStatus && record.status === 'PENDING_APPROVAL' && (
                            <>
                                <PrimaryButton
                                    variant="secondary"
                                    startIcon={<ThumbDownAltOutlined sx={{fontSize: 16}}/>}
                                    onClick={() => { setDialogAction('reject'); setDialogOpen(true); }}
                                    sx={{color: t.color.status.dangerSolid, borderColor: t.color.border.default}}
                                >
                                    {translate('Reject')}
                                </PrimaryButton>
                                <PrimaryButton
                                    variant="primary"
                                    startIcon={<ThumbUpAltOutlined sx={{fontSize: 16}}/>}
                                    onClick={() => { setDialogAction('approve'); setDialogOpen(true); }}
                                >
                                    {translate('Approve')}
                                </PrimaryButton>
                            </>
                        )}
                        {isPhotoPost && (
                            <PrimaryButton
                                variant="ghost"
                                startIcon={<PrintOutlined sx={{fontSize: 16}}/>}
                                onClick={handlePrintPhoto}
                            >
                                {translate('Print')}
                            </PrimaryButton>
                        )}
                        <PrimaryButton
                            variant="primary"
                            startIcon={<EditOutlined sx={{fontSize: 16}}/>}
                            onClick={() => redirect('edit', 'posts', record.id)}
                        >
                            {translate('Edit')}
                        </PrimaryButton>
                        {canDeletePost(permissions, record, identity?.id) && (
                            <PrimaryButton
                                variant="danger"
                                startIcon={<DeleteOutline sx={{fontSize: 16}}/>}
                                onClick={deleteConfirm.openDialog}
                            >
                                {translate('Delete')}
                            </PrimaryButton>
                        )}
                    </>
                }
            />

            <Box sx={{p: 3, display: 'grid', gridTemplateColumns: '1fr 340px', gap: 2, alignItems: 'start'}}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0}}>
                    <MediaBlock record={record} onPrint={handlePrintPhoto} translate={translate}/>

                    <Box
                        sx={{
                            backgroundColor: t.color.surface,
                            border: `1px solid ${t.color.border.default}`,
                            borderRadius: `${t.radius.md}px`,
                            overflow: 'hidden',
                        }}
                    >
                        <StatamicTabs tabs={tabs} value={tab} onChange={setTab}/>
                        <Box sx={{p: 2}}>
                            {tab === 'comments' && <PostComments postId={record.id}/>}
                            {tab === 'translations' && <TranslationTable translations={record.translations}/>}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                    {record.child && (
                        <SectionCard title={translate('Child Information')}>
                            <Box
                                component={RouterLink}
                                to={`/children/${record.child.id}/show`}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 1.5,
                                    borderRadius: `${t.radius.md}px`,
                                    border: `1px solid ${t.color.border.default}`,
                                    textDecoration: 'none',
                                    color: 'inherit',
                                    transition: 'border-color 120ms ease, background-color 120ms ease',
                                    '&:hover': {
                                        borderColor: t.color.border.strong,
                                        backgroundColor: t.color.sidebarHover,
                                    },
                                }}
                            >
                                <Avatar
                                    src={record.child.profile_image && record.child.image_ext
                                        ? `${process.env.REACT_APP_CDN_URL}/${record.child.profile_image}.${record.child.image_ext}`
                                        : undefined}
                                    sx={{
                                        width: 48, height: 48,
                                        bgcolor: t.color.sidebarHover,
                                        color: t.color.text.secondary,
                                        border: `1px solid ${t.color.border.hairline}`,
                                    }}
                                />
                                <Box sx={{flex: 1, minWidth: 0}}>
                                    <Typography sx={{fontSize: `${t.type.bodyStrong.size}px`, fontWeight: 500, color: t.color.text.primary}}>
                                        {record.child.name}
                                    </Typography>
                                    <Typography sx={{fontSize: `${t.type.caption.size}px`, color: t.color.text.secondary}}>
                                        {record.child.charity?.charity_name}
                                    </Typography>
                                </Box>
                            </Box>
                        </SectionCard>
                    )}

                    <SectionCard title={translate('Engagement')}>
                        <MetricRow icon={FavoriteBorder}    label={translate('Likes')}    value={record.nr_of_likes ?? 0}/>
                        <MetricRow icon={ChatBubbleOutline} label={translate('Comments')} value={record.nr_of_comments ?? 0}/>
                        <MetricRow icon={Visibility}        label={translate('Views')}    value={record.views ?? 0}/>
                    </SectionCard>

                    {canManageStatus && (
                        <SectionCard title={translate('Status Information')}>
                            <DataRow label={translate('Current Status')}>
                                <StatusPill variant={statusVariant(record.status)}>
                                    {statusLabel(record.status, translate)}
                                </StatusPill>
                            </DataRow>
                            {record.approved_by && (
                                <DataRow label={record.status === 'PUBLISHED' ? translate('Approved By') : translate('Rejected by')}
                                         value={record.approved_by.given_name}/>
                            )}
                            {record.approved_at && (
                                <DataRow label={record.status === 'PUBLISHED' ? translate('Approved At') : translate('Date')}
                                         value={moment(record.approved_at).format('LLL')}/>
                            )}
                            {record.rejection_reason && (
                                <DataRow label={translate('Rejection Reason')}>
                                    <Typography sx={{fontSize: `${t.type.body.size}px`, color: t.color.status.dangerSolid}}>
                                        {record.rejection_reason}
                                    </Typography>
                                </DataRow>
                            )}
                        </SectionCard>
                    )}

                    {hasPermission(permissions, ['duplicate'], 'post') && (
                        <DuplicatePostAction record={record} translate={translate}/>
                    )}
                </Box>
            </Box>

            <ApprovalDialog
                open={dialogOpen}
                action={dialogAction}
                onClose={() => setDialogOpen(false)}
                onConfirm={(reason) => handleStatusChange(dialogAction, reason)}
                pending={dialogPending}
            />

            <DeleteConfirmDialog
                open={deleteConfirm.open}
                onClose={deleteConfirm.closeDialog}
                resource="posts"
                record={record}
                recordLabel={`post by ${record.creator?.given_name ?? 'unknown'}`}
            />
        </Box>
    );
};

const PostShowStatamic = (props) => (
    <Show {...props} actions={false} component={Box} sx={{backgroundColor: t.color.page}}>
        <PostShowContent/>
    </Show>
);

export default PostShowStatamic;
